import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import EditarLivro from './EditarLivro';
import { useToast } from '../context/ToastContext';
import { Pencil, BookOpen, ArrowLeft, Clock, User } from 'lucide-react';

function DetalheLivro({ livro, onVoltar, onExcluido, onRegistrarEmprestimo }) {
  const { showToast } = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [editando, setEditando] = useState(false);
  const [livroAtual, setLivroAtual] = useState(livro);
  const [emprestimoAtivo, setEmprestimoAtivo] = useState(null);
  const [sinopseExpandida, setSinopseExpandida] = useState(false);

  useEffect(() => {
    if (livroAtual.disponivel) { setEmprestimoAtivo(null); return; }
    supabase
      .from('emprestimos')
      .select('id, leitor_nome, data_devolucao, status, forma_entrega')
      .eq('livro_id', livroAtual.id)
      .in('status', ['pendente', 'aprovado', 'em_andamento'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setEmprestimoAtivo(data || null));
  }, [livroAtual.id, livroAtual.disponivel]);

  async function handleExcluir() {
    setExcluindo(true);
    try {
      const id = livroAtual.id;
      const { data: empAtivo } = await supabase
        .from('emprestimos').select('id').eq('livro_id', id)
        .in('status', ['pendente', 'aprovado', 'em_andamento'])
        .limit(1).maybeSingle();
      if (empAtivo) {
        showToast('Este livro está emprestado. Devolva-o antes de excluir.', 'error');
        setConfirmando(false);
        return;
      }
      await Promise.all([
        supabase.from('emprestimos').delete().eq('livro_id', id),
        supabase.from('clube_livros').delete().eq('livro_id', id),
        supabase.from('interesses').delete().eq('livro_id', id),
        supabase.from('avisos_disponibilidade').delete().eq('livro_id', id),
      ]);
      const { error } = await supabase.from('livros').delete().eq('id', id);
      if (error) throw error;
      showToast('Livro excluído.', 'info');
      onExcluido();
    } catch (err) {
      showToast('Não foi possível remover o livro. Tente novamente.', 'error');
      setConfirmando(false);
    } finally {
      setExcluindo(false);
    }
  }

  if (editando) {
    return (
      <EditarLivro
        livro={livroAtual}
        onVoltar={() => setEditando(false)}
        onSalvo={async () => {
          const { data } = await supabase.from('livros').select('*').eq('id', livroAtual.id).single();
          if (data) setLivroAtual(data);
          setEditando(false);
        }}
      />
    );
  }

  const statusLabel = emprestimoAtivo?.status === 'pendente'
    ? 'Aguardando aprovação'
    : emprestimoAtivo?.status === 'aprovado'
    ? 'Aprovado — aguardando entrega'
    : 'Em andamento';

  const contentSections = (
    <>
      {/* Stars + status row */}
      <div className="flex items-center justify-between py-4 border-b border-line-soft">
        <div className="flex gap-0.5">
          {[1,2,3,4,5].map(s => (
            <span key={s} className={`text-[18px] leading-none ${s <= livroAtual.estrelas ? 'text-amber-400' : 'text-ink-200'}`}>★</span>
          ))}
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${livroAtual.disponivel ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {livroAtual.disponivel ? 'Disponível' : 'Emprestado'}
        </span>
      </div>

      {/* Emprestimo ativo */}
      {!livroAtual.disponivel && emprestimoAtivo && (
        <div className="mt-4 rounded-2xl overflow-hidden border border-amber-100" style={{ background: '#FFFBF0' }}>
          <div className="px-4 pt-3 pb-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600 mb-2">{statusLabel}</p>
          </div>
          <div className="flex items-center gap-3 px-4 pb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FDE68A' }}>
              <User size={14} color="#92400E" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-ink-900 truncate">{emprestimoAtivo.leitor_nome}</p>
              {emprestimoAtivo.data_devolucao && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Clock size={11} color="#B45309" strokeWidth={2} />
                  <p className="text-[11px] text-amber-700">
                    Devolução: {new Date(emprestimoAtivo.data_devolucao + 'T12:00:00').toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Descrição */}
      {livroAtual.descricao && (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-300 mb-2">Sobre o livro</p>
          <p className="text-[14px] text-ink-600 leading-relaxed">
            {sinopseExpandida || livroAtual.descricao.length <= 300
              ? livroAtual.descricao
              : livroAtual.descricao.slice(0, 300).trimEnd() + '...'}
          </p>
          {livroAtual.descricao.length > 300 && (
            <button onClick={() => setSinopseExpandida(v => !v)}
              className="mt-1 text-[13px] font-semibold text-brand hover:underline">
              {sinopseExpandida ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </div>
      )}

      {/* Metadata */}
      {(livroAtual.editora || livroAtual.ano) && (
        <div className="mt-4 rounded-2xl border border-line-soft overflow-hidden" style={{ background: '#fff' }}>
          {livroAtual.editora && (
            <div className="flex justify-between items-center px-4 py-3 border-b border-line-soft">
              <span className="text-[12px] text-ink-400">Editora</span>
              <span className="text-[12px] font-medium text-ink-700">{livroAtual.editora}</span>
            </div>
          )}
          {livroAtual.ano && (
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[12px] text-ink-400">Ano</span>
              <span className="text-[12px] font-medium text-ink-700">{livroAtual.ano}</span>
            </div>
          )}
        </div>
      )}

      {/* Meu comentário */}
      {livroAtual.comentario && (
        <div className="mt-4 rounded-2xl border border-line-soft px-4 py-3" style={{ background: '#fff' }}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-300 mb-2">Minha nota</p>
          <p className="text-[14px] text-ink-600 italic leading-relaxed">"{livroAtual.comentario}"</p>
        </div>
      )}

      {/* Registrar empréstimo */}
      {livroAtual.disponivel && !livroAtual.especial && onRegistrarEmprestimo && (
        <div className="mt-6">
          <button onClick={() => onRegistrarEmprestimo(livroAtual)}
            className="w-full py-3 text-[14px] font-extrabold text-white rounded-2xl border-0 cursor-pointer"
            style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
            Registrar empréstimo
          </button>
        </div>
      )}

      {/* Excluir */}
      <div className="mt-4">
        {!confirmando ? (
          <button onClick={() => setConfirmando(true)}
            className="w-full py-3 text-[13px] font-medium text-red-400 border border-red-100 rounded-2xl hover:bg-red-50 transition-colors">
            Remover da biblioteca
          </button>
        ) : (
          <div className="bg-white border border-red-100 rounded-2xl p-4">
            <p className="text-[14px] font-semibold text-ink-900 mb-1">Remover este livro?</p>
            <p className="text-[12px] text-ink-400 mb-4">Essa ação não pode ser desfeita.</p>
            <div className="flex gap-2">
              <button onClick={handleExcluir} disabled={excluindo}
                className="flex-1 py-2.5 text-[13px] font-medium bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50">
                {excluindo ? 'Removendo...' : 'Sim, remover'}
              </button>
              <button onClick={() => setConfirmando(false)}
                className="flex-1 py-2.5 text-[13px] font-medium border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="bg-paper" style={{ height: '100dvh', overflowY: 'auto' }}>

      {/* ── Mobile layout ── */}
      <div className="lg:hidden">
        {/* Cover hero */}
        <div className="relative w-full" style={{ height: 300 }}>
          {livroAtual.capa_url
            ? <img src={livroAtual.capa_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center" style={{ background: '#E8E4F0' }}>
                <BookOpen size={56} color="#C4BCDA" strokeWidth={1} />
              </div>}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(20,16,30,0.88) 0%, rgba(20,16,30,0.3) 50%, rgba(20,16,30,0.5) 100%)' }} />
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 pt-safe-4 pb-2">
            <button onClick={onVoltar} className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(8px)' }}>
              <ArrowLeft size={18} color="white" strokeWidth={2} />
            </button>
            <button onClick={() => setEditando(true)} className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(8px)' }}>
              <Pencil size={15} color="white" strokeWidth={2} />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 px-5 pb-5">
            <h1 className="text-white text-[22px] font-bold leading-snug mb-1"
              style={{ fontFamily: "'Newsreader', Georgia, serif" }}>{livroAtual.titulo}</h1>
            <p className="text-[13px] font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>{livroAtual.autor}</p>
          </div>
        </div>
        <div className="px-4 pb-10">{contentSections}</div>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden lg:block">
        {/* Top bar */}
        <div className="flex items-center justify-between px-8 py-4 border-b border-line-soft">
          <button onClick={onVoltar} className="flex items-center gap-2 text-[13px] font-semibold text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer">
            <ArrowLeft size={16} strokeWidth={2} /> Voltar
          </button>
          <button onClick={() => setEditando(true)}
            className="flex items-center gap-1.5 text-[13px] font-bold px-4 py-2 rounded-[10px] border-0 cursor-pointer"
            style={{background:'#EDE8FC', color:'#5B3CE4'}}>
            <Pencil size={13} strokeWidth={2} /> Editar
          </button>
        </div>
        {/* Two-column content */}
        <div className="flex gap-10 px-8 py-8 max-w-4xl">
          {/* Cover column */}
          <div className="flex-shrink-0" style={{width: 200}}>
            <div className="rounded-[14px] overflow-hidden" style={{width: 200, height: 290, boxShadow: '0 8px 24px rgba(33,28,43,0.18)'}}>
              {livroAtual.capa_url
                ? <img src={livroAtual.capa_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center" style={{background:'#E8E4F0'}}>
                    <BookOpen size={48} color="#C4BCDA" strokeWidth={1} />
                  </div>}
            </div>
            <h1 className="text-[18px] font-bold text-ink-900 mt-4 leading-snug"
              style={{fontFamily:"'Newsreader', Georgia, serif"}}>{livroAtual.titulo}</h1>
            <p className="text-[13px] text-ink-400 mt-1">{livroAtual.autor}</p>
          </div>
          {/* Content column */}
          <div className="flex-1 min-w-0">{contentSections}</div>
        </div>
      </div>

    </div>
  );
}

export default DetalheLivro;
